import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { publicRequest, privateRequest } from "../utils/api";

// Types based on backend models and API responses
export interface ScholarshipTranslation {
  id?: number;
  scholarship_id?: number;
  locale: string;
  title: string;
  description?: string;
  aim?: string;
  eligibility?: string;
  offers?: string;
  slug: string;
}

export interface ScholarshipSponsor {
  id?: number;
  scholarship_session_id?: number;
  name: string;
  title?: string;
  bio?: string;
  picture?: string;
  is_main: boolean;
}

export interface ScholarshipBeneficiary {
  id?: number;
  scholarship_session_id?: number;
  name: string;
  picture?: string;
  school?: string;
  location?: string;
  class?: string;
  notes?: string;
}

export interface ScholarshipSession {
  id?: number;
  scholarship_id?: number;
  academic_year: string;
  is_active: boolean;
  gallery?: string[];
  sponsors?: ScholarshipSponsor[];
  beneficiaries?: ScholarshipBeneficiary[];
}

export interface MediaFile {
  id: number;
  name: string;
  path: string;
  mime_type: string;
  size: number;
  model_type: string;
  model_id: number;
}

export interface SeoMeta {
  id: number;
  title?: string;
  description?: string;
  keywords?: string;
  model_type: string;
  model_id: number;
}

export interface Scholarship {
  id: number;
  status: string;
  created_by?: number;
  updated_by?: number;
  deleted_by?: number;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string;
  translations?: ScholarshipTranslation[];
  sessions?: ScholarshipSession[];
  media?: MediaFile[];
  seoMeta?: SeoMeta;
}

export interface ScholarshipsResponse {
  scholarships: Scholarship[];
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export interface ScholarshipsQueryParams {
  per_page?: number;
  page?: number;
  locale?: string;
  search?: string;
  status?: string;
}

// Query keys
export const SCHOLARSHIPS_QUERY_KEY = ["scholarships"];
export const SCHOLARSHIP_QUERY_KEY = (id: number) => ["scholarships", id];

// Fetch scholarships with pagination
const fetchScholarships = async (
  params: ScholarshipsQueryParams = {}
): Promise<ScholarshipsResponse> => {
  try {
    const response = await publicRequest.get("/scholarships", { params });

    return response.data.data;
  } catch (error) {
    console.error("Error fetching scholarships:", error);
    throw error;
  }
};

// Fetch single scholarship
const fetchScholarship = async (id: number): Promise<Scholarship> => {
  const response = await publicRequest.get(`/scholarships/${id}`);
  return response.data.data;
};

// Create scholarship (admin only)
const createScholarship = async (
  scholarshipData: Omit<
    Scholarship,
    "id" | "created_at" | "updated_at" | "deleted_at"
  >
): Promise<Scholarship> => {
  try {
    const response = await privateRequest.post(
      "/scholarships",
      scholarshipData
    );
    return response.data.data;
  } catch (error) {
    // Ensure error has a message property
    if (error && typeof error === "object" && !("message" in error)) {
      (error as Error).message =
        "An error occurred while creating the scholarship";
    }
    throw error;
  }
};

// Update scholarship (admin only)
const updateScholarship = async ({
  id,
  scholarshipData,
}: {
  id: number;
  scholarshipData: Partial<Scholarship>;
}): Promise<Scholarship> => {
  const response = await privateRequest.put(
    `/scholarships/${id}`,
    scholarshipData
  );
  return response.data.data;
};

// Delete scholarship (admin only)
const deleteScholarship = async (id: number): Promise<void> => {
  await privateRequest.delete(`/scholarships/${id}`);
};

// Custom hooks
export const useScholarships = (params: ScholarshipsQueryParams = {}) => {
  return useQuery({
    queryKey: [...SCHOLARSHIPS_QUERY_KEY, params],
    queryFn: () => fetchScholarships(params),
    staleTime: 0, // Always refetch on mount
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useScholarship = (id: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: SCHOLARSHIP_QUERY_KEY(id),
    queryFn: () => fetchScholarship(id),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useCreateScholarship = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createScholarship,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SCHOLARSHIPS_QUERY_KEY });
    },
  });
};

export const useUpdateScholarship = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateScholarship,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: SCHOLARSHIPS_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: SCHOLARSHIP_QUERY_KEY(data.id),
      });
    },
  });
};

export const useDeleteScholarship = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteScholarship,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SCHOLARSHIPS_QUERY_KEY });
    },
  });
};

export const useQuickAddScholarshipItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      type,
      session_id,
      data,
    }: {
      id: number;
      type: "sponsor" | "beneficiary";
      session_id: number;
      data: any;
    }) => {
      const response = await privateRequest.post(
        `/scholarships/${id}/quick-add`,
        { type, session_id, data }
      );
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: SCHOLARSHIPS_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: SCHOLARSHIP_QUERY_KEY(variables.id),
      });
    },
  });
};

// Hook to fetch and map a single scholarship for detail view
export const useScholarshipDetail = (id: number) => {
  const { data: apiScholarship, isLoading, error } = useScholarship(id);
  const translation = apiScholarship?.translations?.find(t => t.locale === "en") || apiScholarship?.translations?.[0];

  const mappedScholarship = apiScholarship
    ? {
      id: apiScholarship.id?.toString() || "",
      title: translation?.title || "",
      description: translation?.description || "",
      aim: translation?.aim || "",
      eligibility: translation?.eligibility || "",
      offers: translation?.offers || "",
      status: apiScholarship.status as "active" | "inactive" | "expired",
      sessions: apiScholarship.sessions || [],
    }
    : null;

  return {
    data: mappedScholarship,
    isLoading,
    error,
  };
};
